import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import VehicleFilters from './components/VehicleFilters';
import VehiclesGrid from './components/VehiclesGrid';
import VehicleMapView from './components/VehicleMapView';
import SaveSearchModal from './components/SaveSearchModal';
import SavedSearchesPanel from './components/SavedSearchesPanel';
import { vehicles } from '../../mocks/vehicles';
import { supabase } from '../../lib/supabase';

// Helper function to calculate distance between two coordinates using Haversine formula
function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Helper function to get coordinates for cities
function getCityCoordinates(location: string): { lat: number; lng: number } | null {
  const cityCoords: Record<string, { lat: number; lng: number }> = {
    'Portland, OR': { lat: 45.5152, lng: -122.6784 },
    'Seattle, WA': { lat: 47.6062, lng: -122.3321 },
    'Boise, ID': { lat: 43.6150, lng: -116.2023 },
    'Eugene, OR': { lat: 44.0521, lng: -123.0868 },
    'San Francisco, CA': { lat: 37.7749, lng: -122.4194 },
    'Denver, CO': { lat: 39.7392, lng: -104.9903 },
    'Sacramento, CA': { lat: 38.5816, lng: -121.4944 },
    'Salt Lake City, UT': { lat: 40.7608, lng: -111.8910 },
    'Spokane, WA': { lat: 47.6588, lng: -117.4260 }
  };

  return cityCoords[location] || null;
}

export default function BrowseVehiclesPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMake, setSelectedMake] = useState('All Makes');
  const [selectedModel, setSelectedModel] = useState('All Models');
  const [yearRange, setYearRange] = useState({ min: 2015, max: 2024 });
  const [priceRange, setPriceRange] = useState({ min: 0, max: 100000 });
  const [selectedCondition, setSelectedCondition] = useState('All Conditions');
  const [selectedTransmission, setSelectedTransmission] = useState('All Transmissions');
  const [selectedDrivetrain, setSelectedDrivetrain] = useState('All Drivetrains');
  const [selectedFuelType, setSelectedFuelType] = useState('All Fuel Types');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'map'>('grid');
  const [searchLocation, setSearchLocation] = useState('');
  const [searchRadius, setSearchRadius] = useState(1000);
  const [userCoordinates, setUserCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [locationError, setLocationError] = useState('');
  const [isSaveSearchModalOpen, setIsSaveSearchModalOpen] = useState(false);
  const [isSavedSearchesPanelOpen, setIsSavedSearchesPanelOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  // Check if user is logged in
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setIsLoggedIn(!!user);
    };
    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsLoggedIn(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Get user's current location
  useEffect(() => {
    if (searchLocation === '' || searchLocation === 'Current Location') {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setUserCoordinates({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
            if (searchLocation === '') {
              setSearchLocation('Current Location');
            }
            setLocationError('');
          },
          (error) => {
            console.error('Error getting location:', error);
            setLocationError('Unable to get your location');
          }
        );
      }
    }
  }, [searchLocation]);

  // Geocode search location
  useEffect(() => {
    if (searchLocation && searchLocation !== 'Current Location') {
      // Try to match with known cities first
      const coords = getCityCoordinates(searchLocation);
      if (coords) {
        setUserCoordinates(coords);
        setLocationError('');
      } else {
        // Check if it's a partial match
        const matchingCity = Object.keys({
          'Portland, OR': { lat: 45.5152, lng: -122.6784 },
          'Seattle, WA': { lat: 47.6062, lng: -122.3321 },
          'Boise, ID': { lat: 43.6150, lng: -116.2023 },
          'Eugene, OR': { lat: 44.0521, lng: -123.0868 },
          'San Francisco, CA': { lat: 37.7749, lng: -122.4194 },
          'Denver, CO': { lat: 39.7392, lng: -104.9903 },
          'Sacramento, CA': { lat: 38.5816, lng: -121.4944 },
          'Salt Lake City, UT': { lat: 40.7608, lng: -111.8910 },
          'Spokane, WA': { lat: 47.6588, lng: -117.4260 }
        }).find(city => 
          city.toLowerCase().includes(searchLocation.toLowerCase()) ||
          searchLocation.toLowerCase().includes(city.toLowerCase().split(',')[0])
        );

        if (matchingCity) {
          const matchCoords = getCityCoordinates(matchingCity);
          if (matchCoords) {
            setUserCoordinates(matchCoords);
            setLocationError('');
          }
        } else {
          setLocationError('Location not found. Try: Portland, Seattle, Denver, etc.');
        }
      }
    }
  }, [searchLocation]);

  // Guard against missing or malformed vehicle data
  const safeVehicles = Array.isArray(vehicles) ? vehicles : [];

  // Helper function to determine vehicle category
  const getVehicleCategory = (vehicle: typeof vehicles[0]): string => {
    const model = vehicle?.model?.toLowerCase() || '';
    const make = vehicle?.make?.toLowerCase() || '';
    
    if (model.includes('f-150') || model.includes('silverado')) return 'Truck';
    if (model.includes('wrangler')) return 'SUV';
    if (model.includes('rav4') || model.includes('cx-5') || model.includes('outback')) return 'SUV';
    if (model.includes('model 3') || make.includes('bmw')) return 'Luxury';
    if (model.includes('camry') || model.includes('civic') || model.includes('altima') || model.includes('jetta') || model.includes('3 series')) return 'Sedan';
    if (model.includes('outback')) return 'Wagon';
    
    return 'Sedan'; // default
  };

  const filteredVehicles = safeVehicles
    .filter((vehicle) => {
      // Defensive checks for required fields
      const make = vehicle?.make?.toString() ?? '';
      const model = vehicle?.model?.toString() ?? '';
      const description = vehicle?.description?.toString() ?? '';
      const year = Number(vehicle?.year);
      const estimatedValue = Number(vehicle?.estimatedValue);
      const mileage = Number(vehicle?.mileage);
      const condition = vehicle?.condition?.toString() ?? '';
      const transmission = vehicle?.transmission?.toString() ?? '';
      const drivetrain = vehicle?.drivetrain?.toString() ?? '';
      const fuelType = vehicle?.fuelType?.toString() ?? '';
      const category = getVehicleCategory(vehicle);
      const location = vehicle?.location?.toString() ?? '';

      const matchesSearch =
        make.toLowerCase().includes(searchQuery.toLowerCase()) ||
        model.toLowerCase().includes(searchQuery.toLowerCase()) ||
        description.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesMake = selectedMake === 'All Makes' || make === selectedMake;
      const matchesModel = selectedModel === 'All Models' || model === selectedModel;
      const matchesYear = year >= yearRange.min && year <= yearRange.max;
      const matchesPrice = estimatedValue >= priceRange.min && estimatedValue <= priceRange.max;
      const matchesCondition = selectedCondition === 'All Conditions' || condition === selectedCondition;
      const matchesTransmission =
        selectedTransmission === 'All Transmissions' || transmission.includes(selectedTransmission);
      const matchesDrivetrain = selectedDrivetrain === 'All Drivetrains' || drivetrain === selectedDrivetrain;
      const matchesFuelType = selectedFuelType === 'All Fuel Types' || fuelType === selectedFuelType;
      const matchesCategory = selectedCategory === 'All Categories' || category === selectedCategory;

      // Distance filter
      let matchesDistance = true;
      if (userCoordinates && searchRadius < 1000) {
        const vehicleCoords = getCityCoordinates(location);
        if (vehicleCoords) {
          const distance = calculateDistance(
            userCoordinates.lat,
            userCoordinates.lng,
            vehicleCoords.lat,
            vehicleCoords.lng
          );
          matchesDistance = distance <= searchRadius;
        }
      }

      return (
        matchesSearch &&
        matchesMake &&
        matchesModel &&
        matchesYear &&
        matchesPrice &&
        matchesCondition &&
        matchesTransmission &&
        matchesDrivetrain &&
        matchesFuelType &&
        matchesCategory &&
        matchesDistance
      );
    })
    .sort((a, b) => {
      // Ensure numeric sorting even if fields are missing
      const aYear = Number(a?.year);
      const bYear = Number(b?.year);
      const aPrice = Number(a?.estimatedValue);
      const bPrice = Number(b?.estimatedValue);
      const aMileage = Number(a?.mileage);
      const bMileage = Number(b?.mileage);

      if (sortBy === 'newest') {
        return bYear - aYear;
      } else if (sortBy === 'price-low') {
        return aPrice - bPrice;
      } else if (sortBy === 'price-high') {
        return bPrice - aPrice;
      } else if (sortBy === 'mileage') {
        return aMileage - bMileage;
      } else if (sortBy === 'distance' && userCoordinates) {
        const aCoords = getCityCoordinates(a?.location || '');
        const bCoords = getCityCoordinates(b?.location || '');
        if (aCoords && bCoords) {
          const aDist = calculateDistance(userCoordinates.lat, userCoordinates.lng, aCoords.lat, aCoords.lng);
          const bDist = calculateDistance(userCoordinates.lat, userCoordinates.lng, bCoords.lat, bCoords.lng);
          return aDist - bDist;
        }
      }
      return 0;
    });

  const handleClearFilters = () => {
    setSelectedMake('All Makes');
    setSelectedModel('All Models');
    setYearRange({ min: 2015, max: 2024 });
    setPriceRange({ min: 0, max: 100000 });
    setSelectedCondition('All Conditions');
    setSelectedTransmission('All Transmissions');
    setSelectedDrivetrain('All Drivetrains');
    setSelectedFuelType('All Fuel Types');
    setSelectedCategory('All Categories');
    setSearchQuery('');
    setSearchLocation('');
    setSearchRadius(1000);
  };

  const handleSaveSearch = () => {
    if (!isLoggedIn) {
      setShowLoginPrompt(true);
      return;
    }
    setIsSaveSearchModalOpen(true);
  };

  const handleViewSavedSearches = () => {
    if (!isLoggedIn) {
      setShowLoginPrompt(true);
      return;
    }
    setIsSavedSearchesPanelOpen(true);
  };

  const handleLoadSearch = (search: any) => {
    setSearchQuery(search.search_query || '');
    setSelectedMake(search.make || 'All Makes');
    setSelectedModel(search.model || 'All Models');
    setYearRange({ min: search.year_min, max: search.year_max });
    setPriceRange({ min: search.price_min, max: search.price_max });
    setSelectedCondition(search.condition || 'All Conditions');
    setSelectedTransmission(search.transmission || 'All Transmissions');
    setSelectedDrivetrain(search.drivetrain || 'All Drivetrains');
    setSelectedFuelType(search.fuel_type || 'All Fuel Types');
    setSelectedCategory(search.category || 'All Categories');
    setSearchLocation(search.location || '');
    setSearchRadius(search.radius);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar isScrolled={isScrolled} />

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-3">Browse Vehicles</h1>
                <p className="text-lg text-gray-600">
                  Find your perfect vehicle swap match with advanced filters
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleViewSavedSearches}
                  className="px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-bookmark-line"></i>
                  Saved Searches
                </button>
                <button
                  onClick={handleSaveSearch}
                  className="px-4 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors font-medium whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-save-line"></i>
                  Save Search
                </button>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by make, model, or keyword..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-6 py-4 pr-14 text-base border-2 border-gray-300 rounded-xl focus:outline-none focus:border-teal-500 transition-colors bg-white"
              />
              <button className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer">
                <i className="ri-search-line text-xl"></i>
              </button>
            </div>
            {locationError && (
              <p className="mt-2 text-sm text-amber-600">
                <i className="ri-error-warning-line mr-1"></i>
                {locationError}
              </p>
            )}
          </div>

          <div className="flex gap-6">
            {/* Sidebar Filters */}
            <VehicleFilters
              selectedMake={selectedMake}
              setSelectedMake={setSelectedMake}
              selectedModel={selectedModel}
              setSelectedModel={setSelectedModel}
              yearRange={yearRange}
              setYearRange={setYearRange}
              priceRange={priceRange}
              setPriceRange={setPriceRange}
              selectedCondition={selectedCondition}
              setSelectedCondition={setSelectedCondition}
              selectedTransmission={selectedTransmission}
              setSelectedTransmission={setSelectedTransmission}
              selectedDrivetrain={selectedDrivetrain}
              setSelectedDrivetrain={setSelectedDrivetrain}
              selectedFuelType={selectedFuelType}
              setSelectedFuelType={setSelectedFuelType}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              onClearFilters={handleClearFilters}
              searchLocation={searchLocation}
              setSearchLocation={setSearchLocation}
              searchRadius={searchRadius}
              setSearchRadius={setSearchRadius}
            />

            {/* Main Content */}
            <div className="flex-1">
              {/* Toolbar */}
              <div className="flex items-center justify-between mb-6 bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-gray-600">
                    <strong className="text-gray-900">{filteredVehicles.length}</strong> vehicles found
                    {userCoordinates && searchRadius < 1000 && (
                      <span className="ml-2 text-teal-600">
                        within {searchRadius} miles
                      </span>
                    )}
                  </p>
                </div>

                <div className="flex items-center gap-4">
                  {/* View Mode Toggle */}
                  <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`w-9 h-9 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                        viewMode === 'grid' ? 'bg-white text-teal-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <i className="ri-grid-line text-lg"></i>
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`w-9 h-9 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                        viewMode === 'list' ? 'bg-white text-teal-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <i className="ri-list-check text-lg"></i>
                    </button>
                    <button
                      onClick={() => setViewMode('map')}
                      className={`w-9 h-9 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                        viewMode === 'map' ? 'bg-white text-teal-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <i className="ri-map-pin-line text-lg"></i>
                    </button>
                  </div>

                  {/* Sort Dropdown */}
                  {viewMode !== 'map' && (
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500 cursor-pointer bg-white"
                    >
                      <option value="newest">Newest Year</option>
                      <option value="price-low">Price: Low to High</option>
                      <option value="price-high">Price: High to Low</option>
                      <option value="mileage">Lowest Mileage</option>
                      {userCoordinates && <option value="distance">Nearest First</option>}
                    </select>
                  )}
                </div>
              </div>

              {/* Vehicles Display */}
              {viewMode === 'map' ? (
                <VehicleMapView vehicles={filteredVehicles} />
              ) : (
                <VehiclesGrid vehicles={filteredVehicles} viewMode={viewMode} />
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* Modals */}
      <SaveSearchModal
        isOpen={isSaveSearchModalOpen}
        onClose={() => setIsSaveSearchModalOpen(false)}
        searchCriteria={{
          searchQuery,
          selectedMake,
          selectedModel,
          yearRange,
          priceRange,
          selectedCondition,
          selectedTransmission,
          selectedDrivetrain,
          selectedFuelType,
          selectedCategory,
          searchLocation,
          searchRadius,
        }}
        onSaveSuccess={() => {
          // Show success message
          alert('Search saved successfully! You will be notified when new vehicles match your criteria.');
        }}
      />

      <SavedSearchesPanel
        isOpen={isSavedSearchesPanelOpen}
        onClose={() => setIsSavedSearchesPanelOpen(false)}
        onLoadSearch={handleLoadSearch}
      />

      {/* Login Prompt */}
      {showLoginPrompt && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
              <i className="ri-lock-line text-2xl text-teal-600"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Sign In Required</h2>
            <p className="text-gray-600 mb-6">
              Please sign in to save searches and receive notifications about new vehicles.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLoginPrompt(false)}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowLoginPrompt(false);
                  window.REACT_APP_NAVIGATE('/profile');
                }}
                className="flex-1 px-4 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors font-medium whitespace-nowrap cursor-pointer"
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
